import { FC } from 'react';
import { IonButton, IonCol, IonIcon, IonRow } from '@ionic/react';
import { addCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { useSelector } from 'react-redux';
import { selectCasinoId, selectUserId } from 'appRedux/selectors';
import useFavorites from 'common/reactHooks/useFavorites';
import usePlaylist from 'common/reactHooks/usePlaylist';

interface CompareColumnProps {
  selectedSlots: number[];
  isCompare: boolean;
  onCompareSlot: () => void;
  // "Selected (#)" review button shown on top of the action pills (behind the
  // drawerActionButtons flag). Tapping it filters the list to the selected EGMs.
  // Mirrors the Selected pill pinned in the map's ShortcutBar.
  showSelectedButton?: boolean;
  selectedCount?: number;
  isSelectedView?: boolean;
  onViewSelected?: () => void;
  // "X" pill rendered to the right of the Selected (#) button; clears the whole
  // selected-EGM working set. Styled like the Selected (#) pill.
  onClearSelected?: () => void;
  onAddToPlaylist?: () => void;
  showAddToPlaylist?: boolean;
  playlistActionLabel?: string;
  // When true the playlist pill renders after the Compare pill (used for the
  // "Empty Playlist" action shown when every displayed EGM is selected).
  movePlaylistAfterCompare?: boolean;
  onAddToFavorites?: () => void;
  showAddToFavorites?: boolean;
  favoritesActionLabel?: string;
  // When true the favorites pill renders after the Compare pill (used for the
  // "Empty Favorites" action shown when every displayed EGM is selected).
  moveFavoritesAfterCompare?: boolean;
  // Exits compare mode without clearing the selection (lets the user go back
  // and add more EGMs). Renders as the "Add" pill when isCompare is true.
  onExitCompare?: () => void;
}

const CompareView: FC<CompareColumnProps> = ({
  selectedSlots,
  isCompare,
  onCompareSlot,
  showSelectedButton = false,
  selectedCount = 0,
  isSelectedView = false,
  onViewSelected,
  onClearSelected,
  onAddToPlaylist,
  showAddToPlaylist = false,
  playlistActionLabel = 'Add',
  movePlaylistAfterCompare = false,
  onAddToFavorites,
  showAddToFavorites = false,
  favoritesActionLabel = 'Add',
  moveFavoritesAfterCompare = false,
  onExitCompare,
}) => {
  const userId = useSelector(selectUserId);
  const casinoId = useSelector(selectCasinoId);
  const { handleInsertFavoriteBulk } = useFavorites({});
  const { handleInsertPlaylistBulk } = usePlaylist();

  const handleAddToFavoritesInternal = () => {
    if (!userId || !casinoId) return;
    handleInsertFavoriteBulk(selectedSlots.map(String), userId, String(casinoId));
  };

  const handleAddToPlaylistInternal = () => {
    if (!userId || !casinoId) return;
    handleInsertPlaylistBulk(selectedSlots.map(String), userId, String(casinoId));
  };

  const favoritesButton =
    !isCompare && showAddToFavorites ? (
      <IonButton
        size="small"
        className="compare-btn"
        onClick={onAddToFavorites ?? handleAddToFavoritesInternal}
      >
        <IonIcon
          slot="start"
          className="compare-btn__icon compare-btn__icon--favorite"
          icon="assets/icon/heart-filled-add.svg"
        />
        {favoritesActionLabel}
      </IonButton>
    ) : null;

  const playlistButton =
    !isCompare && showAddToPlaylist ? (
      <IonButton
        size="small"
        className="compare-btn"
        onClick={onAddToPlaylist ?? handleAddToPlaylistInternal}
      >
        <IonIcon
          slot="start"
          className="compare-btn__icon compare-btn__icon--playlist"
          icon="assets/icon/playlist-filled.svg"
        />
        {playlistActionLabel}
      </IonButton>
    ) : null;

  const selectedButton =
    !isCompare && showSelectedButton && selectedCount > 0 && onViewSelected ? (
      <IonButton
        size="small"
        className={`compare-btn compare-btn--selected${
          isSelectedView ? ' compare-btn--active' : ''
        }`}
        onClick={onViewSelected}
      >
        Selected ({selectedCount})
      </IonButton>
    ) : null;

  const clearSelectedButton =
    !isCompare && showSelectedButton && selectedCount > 0 && onClearSelected ? (
      <IonButton
        size="small"
        aria-label="Clear selected EGMs"
        // The X pill keeps its dark default even when the Selected review
        // filter is engaged — it's a clear-action, not a state indicator, so
        // it should not flip to gold alongside the Selected pill.
        className="compare-btn compare-btn--selected compare-btn--clear-x"
        onClick={onClearSelected}
      >

        
        {/* Plain text "X" so the glyph matches the Selected pill's letters
            (same font size as the capital S in "Selected"), not an icon. */}
        X
      </IonButton>
    ) : null;

  const compareButton =
    !isCompare && selectedSlots.length >= 2 ? (
      <IonButton size="small" className="compare-btn" onClick={onCompareSlot}>
        <IonIcon
          slot="start"
          className="compare-btn__icon compare-btn__icon--compare"
          icon="assets/icon/compare-icon.svg"
        />
        Compare
      </IonButton>
    ) : null;

  return (
    <IonRow>
      <IonCol size="12" className="ion-text-center">
        {selectedSlots.length > 0 && (
          <>
            {/* Portrait: the Selected (#) row stacks above the action pills.
                Landscape: compare-view__rows switches to a single row so both
                sets sit on one line with a gap between them (see SCSS). */}
            <div className="compare-view__rows">
              {selectedButton && (
                <div
                  className="compare-view__selected-row"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    // gap: 0 — the clear "X" pill uses negative margin to nest
                    // its concave left edge against the Selected pill's convex
                    // right end with a constant 1px visible gap.
                    gap: 0,
                  }}
                >
                  {selectedButton}
                  {clearSelectedButton}
                </div>
              )}
              <div
                className="compare-view__action-row"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {/* In "empty list" mode the Favorites/Playlist pill becomes the
                    clear-list action and sits on the far left, before the Add
                    and Compare buttons. Otherwise it renders inline before
                    Compare as the normal Add pill. */}
                {moveFavoritesAfterCompare && favoritesButton}
                {movePlaylistAfterCompare && playlistButton}
                {!moveFavoritesAfterCompare && favoritesButton}
                {!movePlaylistAfterCompare && playlistButton}
                {compareButton}
              </div>
            </div>
            {isCompare && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {onClearSelected && (
                  <IonButton
                    size="small"
                    className="compare-btn compare-btn--selected"
                    onClick={onClearSelected}
                  >
                    <IonIcon slot="start" className="compare-btn__icon" icon={closeCircleOutline} />
                    Clear
                  </IonButton>
                )}
                {onExitCompare && (
                  <IonButton
                    size="small"
                    className="compare-btn"
                    onClick={onExitCompare}
                  >
                    <IonIcon slot="start" className="compare-btn__icon" icon={addCircleOutline} />
                    Add
                  </IonButton>
                )}
              </div>
            )}
          </>
        )}
      </IonCol>
    </IonRow>
  );
};

export default CompareView;
